/**
 * Cadena única de playback ID para plantillas Mux-first de la home internacional
 * (`DualCTAVideoLanding`, `InvitacionExclusivaMux`, etc.).
 *
 * Si el admin eligió la plantilla, el visitante SIEMPRE debe ver video Mux —
 * nunca un spinner infinito por falta de ID.
 */

import { getPlaybackIds } from "@/config/bioTemplatePremiumPlans";
import { getMuxCtaUniversalPlaybackId } from "@/config/muxCtaUniversalPlayback";
import { HARDCODED_FALLBACK_PLAYBACK_ID } from "@/config/telegramGroupsVideo";
import {
  isValidMuxPlaybackId,
  resolveInternationalHomeMuxForCountry,
  resolveInternationalHomeMuxVariantForCountry,
  type InternationalHomeMuxByCountryRaw,
  type ResolvedMuxVariant,
} from "@/lib/internationalHomeTemplate";
import {
  parseInternationalHomeMuxByArGeo,
  resolveInternationalHomeMuxForArGeo,
  type InternationalHomeMuxByArGeo,
} from "@/lib/internationalHomeMuxArGeo";

function pickFirstValidPlaybackId(
  ...candidates: (string | null | undefined)[]
): string {
  for (const candidate of candidates) {
    if (isValidMuxPlaybackId(candidate)) return candidate.trim();
  }
  return HARDCODED_FALLBACK_PLAYBACK_ID;
}

export type ResolveInternationalHomeMuxPlaybackOpts = {
  country?: string | null;
  muxByCountry?:
    | InternationalHomeMuxByCountryRaw
    | Record<string, string>
    | null;
  /** Override explícito (prop de Index). Si es válido, pisa el mapa por país. */
  playbackIdOverride?: string | null;
  language?: string;
  /** `simple_mux_overlay_playback_id` desde `/api/premium-invite-config`. */
  runtimePlaybackId?: string | null;
  /** Desde `/api/mux-videos` (meta title + feed + creator_id). */
  muxMetadataPlaybackId?: string | null;
  /** Provincia AR del Worker (`__TEMPLATE_CONFIG__.province`). */
  province?: string | null;
  /** Ciudad AR del Worker (`__TEMPLATE_CONFIG__.city`). */
  city?: string | null;
  /** Mapa admin Mux por provincia/ciudad AR. */
  muxByArGeo?: InternationalHomeMuxByArGeo | null;
};

/** Full resolve including A/B variant identity for analytics. */
export function resolveInternationalHomeMuxPlaybackWithVariant(
  opts: ResolveInternationalHomeMuxPlaybackOpts,
): {
  playbackId: string;
  variant: ResolvedMuxVariant | null;
} {
  const variant = resolveInternationalHomeMuxVariantForCountry(
    opts.country,
    opts.muxByCountry,
  );
  const playbackId = resolveInternationalHomeMuxPlaybackId({
    ...opts,
    // Prefer sticky A/B pick as country override when present
    playbackIdOverride:
      opts.playbackIdOverride || variant?.playbackId || null,
  });
  return { playbackId, variant };
}

/**
 * Resuelve un playback ID garantizado para fullscreen Mux en `/`.
 *
 * Prioridad:
 *   1. override explícito (prop) — si Index ya resolvió AR geo / país
 *   2. AR geo: ciudad > provincia (solo country AR)
 *   3. mapa admin por país
 *   4. config runtime (premium-invite-config)
 *   5. Mux API metadata teaser (`title` + `external_id=feed` + `creator_id`)
 *   6. env VIP por idioma del visitante (`getPlaybackIds`)
 *   7. env VIP EN / ES
 *   8. `VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID`
 *   9. fallback hardcoded del funnel Telegram (siempre válido en Mux)
 */
export function resolveInternationalHomeMuxPlaybackId(
  opts: ResolveInternationalHomeMuxPlaybackOpts,
): string {
  const lang =
    opts.language ||
    (typeof navigator !== "undefined" && navigator.language
      ? navigator.language
      : "en");

  const explicitOverride = isValidMuxPlaybackId(opts.playbackIdOverride)
    ? opts.playbackIdOverride.trim()
    : null;

  const countryCode =
    typeof opts.country === "string" ? opts.country.trim().toUpperCase() : "";
  const arGeo =
    countryCode === "AR"
      ? resolveInternationalHomeMuxForArGeo({
          province: opts.province,
          city: opts.city,
          muxByArGeo:
            opts.muxByArGeo ??
            parseInternationalHomeMuxByArGeo(null),
        }).playbackId
      : null;

  const countryOverride = resolveInternationalHomeMuxForCountry(
    opts.country,
    opts.muxByCountry,
  );
  const envIds = getPlaybackIds(lang);
  const envEnVip = getPlaybackIds("en").vip;
  const envEsVip = getPlaybackIds("es").vip;

  return pickFirstValidPlaybackId(
    explicitOverride,
    arGeo,
    countryOverride,
    opts.runtimePlaybackId,
    opts.muxMetadataPlaybackId,
    envIds.vip,
    envEnVip,
    envEsVip,
    getMuxCtaUniversalPlaybackId(),
    HARDCODED_FALLBACK_PLAYBACK_ID,
  );
}

export type InternationalHomeMuxPlaybackSource =
  | "explicit"
  | "ar_city"
  | "ar_province"
  | "country"
  | "runtime"
  | "mux_metadata"
  | "env"
  | "universal"
  /**
   * `brand_default` = último recurso (`BRAND_MUX_DEFAULT_PLAYBACK_ID` de brandConfig).
   * Verlo en PostHog significa que NO había override admin por país, config runtime,
   * metadata Mux ni env de idioma → señal de config incompleta para ese visitante.
   */
  | "brand_default"
  /** @deprecated conservado por compatibilidad; el resolver ya emite `brand_default`. */
  | "hardcoded";

/** Origen del playback ID que quedó efectivo (para analytics / admin debug). */
export function resolveInternationalHomeMuxPlaybackSource(
  playbackId: string,
  opts: ResolveInternationalHomeMuxPlaybackOpts,
): InternationalHomeMuxPlaybackSource {
  const lang =
    opts.language ||
    (typeof navigator !== "undefined" && navigator.language
      ? navigator.language
      : "en");
  const envIds = getPlaybackIds(lang);

  const countryCode =
    typeof opts.country === "string" ? opts.country.trim().toUpperCase() : "";
  const arMatch =
    countryCode === "AR"
      ? resolveInternationalHomeMuxForArGeo({
          province: opts.province,
          city: opts.city,
          muxByArGeo: opts.muxByArGeo,
        })
      : { playbackId: null, level: "none" as const };

  const chain: { source: InternationalHomeMuxPlaybackSource; id: string | null | undefined }[] =
    [
      {
        source: "explicit",
        id: isValidMuxPlaybackId(opts.playbackIdOverride)
          ? opts.playbackIdOverride
          : null,
      },
      {
        source: "ar_city",
        id: arMatch.level === "city" ? arMatch.playbackId : null,
      },
      {
        source: "ar_province",
        id: arMatch.level === "province" ? arMatch.playbackId : null,
      },
      {
        source: "country",
        id: resolveInternationalHomeMuxForCountry(opts.country, opts.muxByCountry),
      },
      { source: "runtime", id: opts.runtimePlaybackId },
      { source: "mux_metadata", id: opts.muxMetadataPlaybackId },
      { source: "env", id: envIds.vip },
      { source: "env", id: getPlaybackIds("en").vip },
      { source: "env", id: getPlaybackIds("es").vip },
      { source: "universal", id: getMuxCtaUniversalPlaybackId() },
      { source: "brand_default", id: HARDCODED_FALLBACK_PLAYBACK_ID },
    ];

  for (const { source, id } of chain) {
    if (isValidMuxPlaybackId(id) && id.trim() === playbackId) return source;
  }
  return "brand_default";
}

/**
 * Override efectivo para props de Index → DualCTA / Invitacion.
 * Prioridad: AR ciudad > AR provincia > país.
 */
export function resolveMuxPlaybackOverrideForVisitor(opts: {
  country?: string | null;
  province?: string | null;
  city?: string | null;
  muxByCountry?:
    | InternationalHomeMuxByCountryRaw
    | Record<string, string>
    | null;
  muxByArGeo?: InternationalHomeMuxByArGeo | null;
}): string | null {
  const countryCode =
    typeof opts.country === "string" ? opts.country.trim().toUpperCase() : "";

  if (countryCode === "AR") {
    const ar = resolveInternationalHomeMuxForArGeo({
      province: opts.province,
      city: opts.city,
      muxByArGeo: opts.muxByArGeo,
    });
    if (ar.playbackId) return ar.playbackId;
  }

  return resolveInternationalHomeMuxForCountry(opts.country, opts.muxByCountry);
}
